import { Component, OnInit, Input, Output, EventEmitter, HostListener, OnDestroy } from '@angular/core';
import { fadeInUp400ms, scaleYOut160ms } from '../../../../animations';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { StrategiesService, Indicator } from '../../../../providers';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { StrategyParamsFormComponent } from '../strategy-params-form/strategy-params-form.component';
import { SearchSymbolResults } from '../../../../providers/symbol-search/symbol-search';
import { Subscription, merge, BehaviorSubject } from 'rxjs';
import { SymbolSearchService } from '@app/_shared/providers';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { AND_OR, COMPARATORS, StrategyFormBuildMode } from '../../strategy.enums';
import { IndicatorLogicI } from '@app/_shared/providers/strategies/strategies.interface';
import { ADVANCE_INDICATOR_INPUT_TYPE } from '../advance-indicator-form/advance-indicator-form.enums';
import { Utils, DEFAULT_PRECISION, FormControlErrorMatcher } from '@app/_helpers';
import { MAX_ABSOLUTE_VALUE } from '@app/_helpers/constants';
import { find, isNumber } from 'lodash';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';

@Component({
  selector: 'app-basic-indicator-form',
  templateUrl: './basic-indicator-form.component.html',
  styleUrls: ['./basic-indicator-form.component.scss'],
  animations: [
    fadeInUp400ms,
    scaleYOut160ms
  ]
})
export class BasicIndicatorFormComponent implements OnInit, OnDestroy {
  StrategyFormBuildMode = StrategyFormBuildMode;
  @Input() strategyMode$: BehaviorSubject<StrategyFormBuildMode>;
  @Output() updatedForm = new EventEmitter();
  searchedSymbols: SearchSymbolResults[] = [];
  symbolInputSubRef: Subscription;
  MAX_ABSOLUTE_VALUE = MAX_ABSOLUTE_VALUE;

  COMPARATORS = COMPARATORS;
  AND_OR = AND_OR;

  errMatcher = new FormControlErrorMatcher();
  indicatorList: Indicator[] = [];
  filteredIndicators: string[] = [];

  formGroupArr: Array<UntypedFormGroup> = [this.generateNewFormGroup()];
  rowCondition: Array<UntypedFormControl> = [];
  selectedSymbols: SearchSymbolResults[] = [];
  removeDisabled: boolean;
  valueChangeSubscription: Subscription;

  constructor(
    private strategyService: StrategiesService,
    private dialog: MatDialog,
    private symbolSearchService: SymbolSearchService
  ) { }

  ngOnInit(): void {
    this.getIndicators();
    this.listenSymbolInputs();
  }


  ngOnDestroy(): void {
    this.valueChangeSubscription?.unsubscribe();
  }

  getIndicators() {
    this.strategyService.getIndicatorsList().subscribe(res => {
      this.indicatorList = res;
    })
  }

  /**
   * ===== TICKET ====
   * Done For RA-544
   *
   * ==== FIX =====
   *
   * Whenever reference symbol checkbox is changed we reset the reference
   * input of that array. and remove that symbol from selectedSymbols Array.
   *
   * @param {MatCheckboxChange} event MatCheckBox Event Triggered
   * @param {number} idx Index of formArray which is changed
   */
  checkIsSymbolChange(event: MatCheckboxChange, idx: number) {
    const referenceSymbolControl = this.formGroupArr[idx].get('symbol');
    if (event.checked) {
      referenceSymbolControl.setValidators([Validators.required]);
    } else {
      referenceSymbolControl.clearValidators();
    }
    referenceSymbolControl.updateValueAndValidity();
    referenceSymbolControl.reset();
    this.selectedSymbols.splice(idx, 1);
  }

  generateNewFormGroup() {
    return new UntypedFormGroup({
      isSymbolEnabled: new UntypedFormControl(false),
      symbol: new UntypedFormControl(null),
      indicatorA: new UntypedFormControl(null, [Validators.required]),
      // valueA: new FormControl(null, [Validators.required]),
      comparator: new UntypedFormControl(null, [Validators.required]),
      indicatorB: new UntypedFormControl(null, [Validators.required]),
      // valueB: new FormControl(null, [Validators.required]),
    });
  }

  checkFilterEvent(event: any) {
    const value: string = event.target.value;
    const key = event.key;

    // Allow special key presses
    if (event.ctrlKey || event.metaKey ||
      key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown' ||
      key === 'Backspace' || key === 'Delete' || key === 'Tab' || key === 'Left' || key === 'Right') {
      return;
    }

    if (value && !this.isNotNumber(key)) {
      const decimalPlaces = value.split('.')[1] || '';

      /**
        * ===== TICKET ====
        * Done For RA-1004
        *
        * ==== FIX =====
        *
        * Whenever user enter a number after a decimal, whose position is 1 less than
        * DEFAULT_PRECISION, the user cannot do so
        *   
        * example-Consider value in the field 123.43
        * If a user tries to enter after decimal, he will not be allowed to do so
        * However he can add as many digits he want before decimal
        */

      if (decimalPlaces.length >= DEFAULT_PRECISION &&
        event.target.selectionStart > (value.length - DEFAULT_PRECISION - 1)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    const newCharacter = String.fromCharCode(event.charCode || event.keyCode);
    this.filterIndicators(value + newCharacter);
  }

  filterIndicators(indicator) {
    if (indicator == null || indicator === "") {
      this.getIndicators();
    } else {
      this.getIndicators();
      this.indicatorList = this.indicatorList.filter(i => i.name.toLowerCase().includes(indicator.toLowerCase()));
    }
  }

  generateNewRowComparator() {
    return new UntypedFormControl(AND_OR.AND, [Validators.required]);
  }

  addForm() {
    this.formGroupArr.push(this.generateNewFormGroup());
    this.rowCondition.push(this.generateNewRowComparator());
    this.listenSymbolInputs();
    this.removeDisabled = false;
  }

  removeForm() {
    if (this.formGroupArr.length === 1) {
      this.formGroupArr[0].reset();
      this.removeDisabled = true;
      // If the first row is touched, then enable the clear button
      if (!this.valueChangeSubscription) {
        this.valueChangeSubscription = this.formGroupArr[0]?.valueChanges.subscribe((res) => {
          this.removeDisabled = false;
        });
      }
      return;
    }
    this.removeDisabled = false;
    this.formGroupArr.pop().reset();
    this.rowCondition.pop().reset();
  }

  openValueDialog(indicatorRowIndex: number, isIndicatorB = false, indicatorTrigger) {
    let indicator: string;
    if (isIndicatorB) {
      indicator = this.formGroupArr[indicatorRowIndex].get('indicatorB').value;
    } else {
      indicator = this.formGroupArr[indicatorRowIndex].get('indicatorA').value;
    }
    if (!indicator) {
      return;
    }
    const indicatorObject = find(this.strategyService.params, (params) => params.name === indicator);
    if (!indicatorObject.inputParamterObjects.length) {
      const valueStr = Utils.formatIndicatorValueString(indicator);
      if (isIndicatorB) {
        this.formGroupArr[indicatorRowIndex].get('indicatorB').setValue(valueStr);
      } else {
        this.formGroupArr[indicatorRowIndex].get('indicatorA').setValue(valueStr);
      }
      return;
    }
    this.updatedForm.emit();
    const dialog = this.dialog.open(StrategyParamsFormComponent, {
      data: indicator
    });

    dialog.afterClosed().subscribe(data => {

      const valueStr = Utils.formatIndicatorValueString(indicator, Object.values(data));
      indicatorTrigger.closePanel();

      if (isIndicatorB) {
        this.formGroupArr[indicatorRowIndex].get('indicatorB').setValue(valueStr);
      } else {
        this.formGroupArr[indicatorRowIndex].get('indicatorA').setValue(valueStr);
      }
      this.updatedForm.emit();
    });
  }

  listenSymbolInputs() {
    if (this.symbolInputSubRef) {
      this.symbolInputSubRef.unsubscribe();
    }

    this.symbolInputSubRef = merge(
      ...this.formGroupArr.map(form => form.get('symbol').valueChanges)
    ).subscribe(input => {
      this.searchedSymbols = [];
      if (!input || !input.length) { return; }
      // Search indices too.
      this.searchedSymbols = this.symbolSearchService.search(input, false, true);
    })
  }

  addSymbol(event: MatAutocompleteSelectedEvent, idx: number) {
    this.selectedSymbols[idx] = event.option.value;
    this.formGroupArr[idx].get('symbol').setValue(event.option.value.market_id + " " + event.option.value.scrip_name);
  }

  validateRefSymbol(idx: number) {
    // setting a timeout so that addSymbol function above has enough time to set value of 'symbol' field
    // before search begins
    setTimeout(() => {
      const inputCtrl = this.formGroupArr[idx].get('symbol');
      const found = find(this.searchedSymbols, (symbol) => {
        return (symbol.market_id + " " + symbol.scrip_name) === inputCtrl.value;
      });

      if (!found && this.formGroupArr[idx].get('isSymbolEnabled').value) {
        inputCtrl.setValue('');
        inputCtrl.setValidators([Validators.required]);
        inputCtrl.updateValueAndValidity();
      }
    }, 300)
  }

  fillUpdateForm(logic: IndicatorLogicI) {
    // this.selectedSymbols = this.symbolSearchService.findScripsBySecurity(logic.indicatorRow
    //   .filter(i => i.symbol)
    //   .map(i => {
    //     return {
    //       security_id: i.symbol.security_id,
    //       market_id: i.symbol.market_id
    //     }
    //   }));
    /**
     * formGroupArr set to its default value so that it does not show empty rows 
     * when edit button is clicked   
     */

    this.formGroupArr = [this.generateNewFormGroup()];
    logic.indicatorRow.forEach((row, idx) => {
      const indicatorAParams = Object.values(row.indicators[0].params);
      this.formGroupArr[idx].patchValue({
        isSymbolEnabled: !!row.symbol,
        symbol: row.symbol ? this.selectedSymbols[idx].scrip_name : null,
        indicatorA: isNaN(row.indicators[0].name as any) ?
          Utils.formatIndicatorValueString(row.indicators[0].name, indicatorAParams.length ? indicatorAParams : null) :
          row.indicators[0].name,
        comparator: row.indicators[1].name,
        indicatorB: isNaN(row.indicators[2].name as any) ? Utils.formatIndicatorValueString(row.indicators[2].name, Object.values(row.indicators[2].params)) : row.indicators[2].name,
      });
      if (idx < (logic.indicatorRow.length - 1)) {
        this.addForm();
        this.rowCondition[idx].setValue(logic.rowComparator[idx]);
      }
    });

    this.listenSymbolInputs();
  }

  validateComparatorInput(formIndex: number) {
    const inputCtrl = this.formGroupArr[formIndex].get('comparator');
    const comparatorFound = Object.values(COMPARATORS).find(v => v.toLowerCase() === inputCtrl.value?.toLowerCase());

    if (!comparatorFound) {
      inputCtrl.reset();
    } else {
      inputCtrl.setValue(comparatorFound);
    }
    inputCtrl.markAllAsTouched();
    this.updatedForm.emit();
  }

  validateIndicatorInput(formIndex: number, indicatorB = false) {
    const indicatorInput = this.formGroupArr[formIndex].get(indicatorB ? 'indicatorB' : 'indicatorA');
    if (!this.isNotNumber(indicatorInput.value)) {
      indicatorInput.setValidators([Validators.required, Validators.min(0.01), Validators.max(MAX_ABSOLUTE_VALUE)]);
      indicatorInput.updateValueAndValidity();

      return;
    }
    const indicatorObj = this.strategyService.params.find((i) => i.name.toLowerCase() === indicatorInput.value?.split('(')[0].toLowerCase());
    if (!indicatorObj) {
      indicatorInput.setValue('');
    }
    this.updatedForm.emit();
  }

  @HostListener('keydown')
  keyPressed() {
    this.updatedForm.emit();
  }

  getData(): IndicatorLogicI {
    if (!this.isValid) { return; }

    return {
      rowComparator: this.rowCondition.map(form => form.value),
      indicatorRow: this.formGroupArr.map((form, idx) => {
        const rowForm = form.value;
        let symbol = null;
        if (this.selectedSymbols[idx]) {
          symbol = {
            market_id: this.selectedSymbols[idx].market_id,
            security_id: this.selectedSymbols[idx].security_id
          }
        }
        let indiA;
        const isIndiAValue = !isNaN(rowForm.indicatorA);
        if (!isIndiAValue) {
          indiA = Utils.parseIndicatorValueString(rowForm.indicatorA);
        }
        let indiB;
        const isIndiBValue = !isNaN(rowForm.indicatorB);
        if (!isIndiBValue) {
          indiB = Utils.parseIndicatorValueString(rowForm.indicatorB);
        }
        return {
          indicators: [
            {
              name: isIndiAValue ? rowForm.indicatorA : indiA.name,
              type: isIndiAValue ? ADVANCE_INDICATOR_INPUT_TYPE.NUMBER : ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR,
              params: indiA?.params || []
            },
            {
              name: rowForm.comparator,
              type: ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR,
              params: []
            },
            {
              name: isIndiBValue ? rowForm.indicatorB : indiB.name,
              type: isIndiBValue ? ADVANCE_INDICATOR_INPUT_TYPE.NUMBER : ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR,
              params: indiB?.params || []
            }
          ],
          symbol
        }
      })
    }
  }

  isNotNumber(val: any) {
    return isNaN(val) || val?.toString().includes(' ');
  }

  get isValid() {
    let isValidForms = true;
    this.formGroupArr.forEach(g => isValidForms = isValidForms && g.valid);
    if (!isValidForms) { return isValidForms; }
    this.rowCondition.forEach(f => isValidForms = isValidForms && f.valid);
    return isValidForms;
  }
}
